import { useState } from 'react';
import { demander } from '@/components/ui/Fenetres';
import { CheckCircle2, FileSpreadsheet, Lock, RefreshCw, Unlock } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/stores/toastStore';
import { useAuthStore } from '@/stores/authStore';
import { formatMontant } from '@/utils/currency';
import { exportToXLSX, buildExportFilename } from '@/utils/exports';
import { etatApi, type EtatMensuelLigne } from '@/api/pointage';
import { LIBELLE_MOTIF } from './PresenceJour';

/**
 * Pointage P4 — état mensuel (docs/PLAN-POINTAGE.md). Décisions PDG du 16/10/2026 : retard seulement noté ;
 * absence non justifiée retenue au salaire ÷ 26 ; 48 h comprises ; heures supplémentaires autorisées par le DAF ;
 * validation par le DAF, mois figé ensuite. Montants : DAF et PDG seulement.
 */
const moisPrecedent = () => { const d = new Date(); d.setUTCDate(1); d.setUTCMonth(d.getUTCMonth() - 1); return d.toISOString().slice(0, 7); };
const moisCourant = () => new Date().toISOString().slice(0, 7);
const h = (min: number) => (min ? `${Math.floor(min / 60)} h ${String(min % 60).padStart(2, '0')}` : '—');
const f = (cents: string | null) => (cents == null ? '—' : `${formatMontant(cents)} F`);
const dateHeure = (iso: string | null) => (iso ? new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '');
const libelleMois = (m: string) => new Date(`${m}-01T00:00:00Z`).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric', timeZone: 'UTC' });

export const EtatMensuelPointage = () => {
  const role = useAuthStore((s) => s.user?.role);
  const filiale = useAuthStore((s) => s.filiale);
  const estDaf = role === 'daf' || role === 'admin_holding';
  const peutCalculer = estDaf || role === 'rh';
  const qc = useQueryClient();
  const [mois, setMois] = useState(moisPrecedent());
  const q = useQuery({ queryKey: ['hr', 'pointage', 'etat', mois], queryFn: () => etatApi.lire(mois) });
  const maj = (d: unknown) => qc.setQueryData(['hr', 'pointage', 'etat', mois], d);

  const calculer = useMutation({ mutationFn: () => etatApi.calculer(mois), onSuccess: (d) => { maj(d); toast.success('État calculé', libelleMois(mois)); } });
  const valider  = useMutation({ mutationFn: () => etatApi.valider(mois),  onSuccess: (d) => { maj(d); toast.success('Mois validé', `${libelleMois(mois)} est figé`); } });
  const rouvrir  = useMutation({
    mutationFn: (motif: string) => etatApi.rouvrir(mois, motif),
    onSuccess: (d) => { maj(d); toast.success('Mois rouvert', 'Les corrections sont de nouveau possibles'); },
  });
  const demanderValidation = async () => {
    if (await demander.confirmer({ titre: `Valider l'état de ${libelleMois(mois)} ?`, message: 'Plus aucun pointage, correction ou absence ne pourra modifier ce mois (sauf réouverture motivée).', confirmer: 'Valider', danger: true })) valider.mutate();
  };
  const corriger = useMutation({
    mutationFn: ({ employeeId, l }: { employeeId: string; l: { joursPrevus: number; joursPresents: number; suspendu: boolean } }) => etatApi.corrigerRattrapage(mois, employeeId, l),
    onSuccess: (d) => { maj(d); toast.success('Ligne corrigée'); },
  });
  const demanderCorrection = async (l: EtatMensuelLigne) => {
    const p = await demander.texte({ titre: l.employee.fullName, libelle: `Jours présents sur ${l.joursPrevus}`, defaut: String(l.joursPresents), confirmer: 'Corriger' });
    if (p == null || !/^\d{1,2}$/.test(p.trim()) || Number(p) > l.joursPrevus) return;
    const suspendu = await demander.confirmer({ titre: 'Les jours non travaillés sont-ils une SUSPENSION du contrat ?', message: '« Suspension » = suspension du contrat. « Annuler » = absences retenues sur salaire.', confirmer: 'Suspension' });
    corriger.mutate({ employeeId: l.employeeId, l: { joursPrevus: l.joursPrevus, joursPresents: Number(p), suspendu } });
  };
  const demanderReouverture = async () => {
    const motif = await demander.texte({ titre: `Rouvrir ${libelleMois(mois)} ?`, libelle: 'Motif (5 caractères au moins)', min: 5, confirmer: 'Rouvrir', danger: true });
    if (motif === null) return;
    rouvrir.mutate(motif);
  };

  const d = q.data;
  const valide = d?.etat?.statut === 'valide';
  const montants = !!d?.montantsVisibles;
  const lignes = d?.lignes ?? [];
  const rattrapage = d?.etat?.origine === 'rattrapage';
  const incomplets = lignes.filter((l) => l.incomplets > 0);
  const totalRetenues = lignes.reduce((s, l) => s + Number(l.retenueAbsenceCents ?? 0), 0);

  const exporter = () => {
    void exportToXLSX<EtatMensuelLigne>({
      filename:  buildExportFilename(`Etat-pointage-${mois}`, filiale?.filialeKey, 'xlsx'),
      sheetName: `État ${mois}`,
      rows:      lignes,
      columns: [
        { header: 'Matricule', accessor: (l) => l.employee.matricule },
        { header: 'Employé',   accessor: (l) => l.employee.fullName },
        { header: 'Service',   accessor: (l) => l.employee.department },
        { header: 'Jours prévus', accessor: (l) => l.joursPrevus, align: 'right' },
        { header: 'Jours présents', accessor: (l) => l.joursPresents, align: 'right' },
        { header: 'Absences non justifiées (j)', accessor: (l) => l.absencesNonJustifiees, align: 'right' },
        { header: 'Absences justifiées (j)', accessor: (l) => l.absencesJustifiees, align: 'right' },
        { header: 'Suspension du contrat (j)', accessor: (l) => l.joursSuspension, align: 'right' },
        { header: 'Détail justifiées', accessor: (l) => Object.entries(l.justifieesParMotif).map(([m, n]) => `${LIBELLE_MOTIF[m] ?? m} ${n}`).join(', ') },
        { header: 'Retards (nb)', accessor: (l) => l.nbRetards, align: 'right' },
        { header: 'Retards (min, notés)', accessor: (l) => l.retardTotalMin, align: 'right' },
        { header: 'Départs avant l’heure (min)', accessor: (l) => l.departsAvantTotalMin, align: 'right' },
        { header: 'Heures sup. comptées (min)', accessor: (l) => l.hsCompteesMin, align: 'right' },
        { header: 'dont repos / férié (min)', accessor: (l) => l.hsReposCompteesMin, align: 'right' },
        ...(montants ? [
          { header: 'Salaire (F)', accessor: (l: EtatMensuelLigne) => (l.salaireCents ? Number(l.salaireCents) / 100 : ''), align: 'right' as const },
          { header: 'Retenue absences (F)', accessor: (l: EtatMensuelLigne) => (l.retenueAbsenceCents ? Number(l.retenueAbsenceCents) / 100 : ''), align: 'right' as const },
          { header: 'Heures sup. (F)', accessor: (l: EtatMensuelLigne) => (l.montantHsCents == null ? 'selon décision PDG' : Number(l.montantHsCents) / 100), align: 'right' as const },
        ] : []),
      ],
    });
  };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
        <input type="month" className="ds-fi" style={{ width: 'auto' }} value={mois} max={moisCourant()} onChange={(e) => e.target.value && setMois(e.target.value)} />
        <span style={{ fontSize: 13, textTransform: 'capitalize' }}>{libelleMois(mois)}</span>
        {d?.etat ? (
          valide
            ? <span className="ds-bd ds-bd-s"><Lock size={11} style={{ verticalAlign: -1 }} /> Validé par {d.etat.validePar ?? 'le DAF'} le {dateHeure(d.etat.valideLe)}</span>
            : <span className="ds-bd ds-bd-w">Brouillon, calculé le {dateHeure(d.etat.calculeLe)}</span>
        ) : <span className="ds-bd ds-bd-n">Pas encore calculé</span>}
        <span style={{ flex: 1 }} />
        {peutCalculer && !valide && !rattrapage && (
          <button className="ds-btn ds-btn-g ds-btn-sm" disabled={calculer.isPending} onClick={() => calculer.mutate()}>
            <RefreshCw size={14} /> {d?.etat ? 'Recalculer' : 'Calculer'}
          </button>
        )}
        {estDaf && !valide && d?.etat && mois < moisCourant() && (
          <button className="ds-btn ds-btn-p ds-btn-sm" disabled={valider.isPending || incomplets.length > 0} onClick={demanderValidation}
            title={incomplets.length ? 'Corrigez d’abord les pointages incomplets' : ''}>
            <CheckCircle2 size={14} /> Valider le mois
          </button>
        )}
        {estDaf && valide && (
          <button className="ds-btn ds-btn-g ds-btn-sm" disabled={rouvrir.isPending} onClick={demanderReouverture}><Unlock size={14} /> Rouvrir</button>
        )}
        <button className="ds-btn ds-btn-g ds-btn-sm" disabled={!lignes.length} onClick={exporter}><FileSpreadsheet size={14} /> Excel</button>
      </div>

      {rattrapage && (
        <div style={{ background: '#E0F2FE', color: '#0C4A6E', padding: '8px 12px', borderRadius: 8, fontSize: 13 }}>
          <strong>Rattrapage</strong> — état saisi d'après « {d?.etat?.source} », sans pointage : il ne se recalcule pas.
          {estDaf && !valide && ' Cliquez sur une ligne pour corriger un chiffre avant de valider.'}
        </div>
      )}
      {d?.etat?.motifReouverture && !valide && (
        <div style={{ fontSize: 12, color: 'var(--tx-2)' }}>Rouvert par {d.etat.rouvertPar} le {dateHeure(d.etat.rouvertLe)} : {d.etat.motifReouverture}</div>
      )}
      {mois === moisCourant() && <div style={{ fontSize: 12, color: 'var(--tx-2)' }}>Mois en cours : l'état se calcule pour suivre, il se valide une fois le mois terminé.</div>}
      {incomplets.length > 0 && !valide && (
        <div style={{ background: '#FEF3C7', color: '#78350F', padding: '8px 12px', borderRadius: 8, fontSize: 13 }}>
          Pointages incomplets à corriger avant validation (onglet Présence du jour) : {incomplets.map((l) => `${l.employee.fullName} (${l.incomplets})`).join(', ')}.
        </div>
      )}
      {d?.regles && montants && (
        <div style={{ fontSize: 12, color: 'var(--tx-2)' }}>
          Retenue d'un jour d'absence non justifiée : salaire ÷ {d.regles.diviseurAbsence}. Heures supplémentaires :
          {d.regles.majorationHsPct == null ? ' majoration selon décision PDG — heures comptées, non chiffrées.' : ` majoration ${d.regles.majorationHsPct} %`}
          {d.regles.majorationHsPct != null && (d.regles.majorationHsReposPct == null ? ' (repos/férié : selon décision PDG).' : `, repos/férié ${d.regles.majorationHsReposPct} %.`)}
          {montants && ` Total des retenues : ${formatMontant(totalRetenues)} F.`}
        </div>
      )}

      <div className="ds-table-panel">
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table className="ds-tbl" style={{ minWidth: montants ? 1040 : 820 }}>
            <thead>
              <tr>
                <th>Employé</th>
                <th style={{ textAlign: 'right' }}>Prévus</th><th style={{ textAlign: 'right' }}>Présents</th>
                <th style={{ textAlign: 'right' }}>Absences non just.</th><th>Absences justifiées</th>
                <th style={{ textAlign: 'right' }}>Retards (notés)</th><th style={{ textAlign: 'right' }}>Incomplets</th>
                <th style={{ textAlign: 'right' }}>Heures sup.</th>
                {montants && <><th style={{ textAlign: 'right' }}>Retenue</th><th style={{ textAlign: 'right' }}>Montant HS</th></>}
              </tr>
            </thead>
            <tbody>
              {q.isLoading && <tr><td colSpan={10}>Chargement…</td></tr>}
              {!q.isLoading && !lignes.length && (
                <tr><td colSpan={10} style={{ textAlign: 'center', padding: 24, color: 'var(--tx-3)' }}>
                  {d?.etat ? 'Aucun employé ce mois-ci' : peutCalculer ? 'Cliquez sur « Calculer » pour établir l’état du mois' : 'État non encore calculé'}
                </td></tr>
              )}
              {lignes.map((l) => (
                <tr key={l.id} onClick={rattrapage && estDaf && !valide ? () => demanderCorrection(l) : undefined}
                  style={rattrapage && estDaf && !valide ? { cursor: 'pointer' } : undefined} title={rattrapage && estDaf && !valide ? 'Corriger cette ligne' : undefined}>
                  <td><strong>{l.employee.fullName}</strong><div style={{ fontSize: 11, color: 'var(--tx-3)' }}>{l.employee.department} · {l.employee.matricule}</div></td>
                  <td style={{ textAlign: 'right' }}>{l.joursPrevus}</td>
                  <td style={{ textAlign: 'right' }}>{l.joursPresents}{l.joursReposTravailles ? <div style={{ fontSize: 11 }}>+ {l.joursReposTravailles} repos</div> : null}</td>
                  <td style={{ textAlign: 'right', color: l.absencesNonJustifiees ? 'var(--danger)' : undefined, fontWeight: l.absencesNonJustifiees ? 700 : 400 }}>{l.absencesNonJustifiees}</td>
                  <td style={{ fontSize: 12 }}>
                    {l.absencesJustifiees ? Object.entries(l.justifieesParMotif).map(([m, n]) => `${LIBELLE_MOTIF[m] ?? m} ${n}`).join(', ') : l.joursSuspension ? '' : '—'}
                    {l.joursSuspension > 0 && <span className="ds-bd ds-bd-n">suspension {l.joursSuspension} j</span>}
                  </td>
                  <td style={{ textAlign: 'right' }}>{l.nbRetards ? `${l.nbRetards} · ${h(l.retardTotalMin)}` : '—'}</td>
                  <td style={{ textAlign: 'right', color: l.incomplets ? 'var(--danger)' : undefined }}>{l.incomplets || '—'}</td>
                  <td style={{ textAlign: 'right' }} title={`Faites ${h(l.hsFaitesMin)} · autorisées ${h(l.hsAutoriseesMin)}`}>{h(l.hsCompteesMin)}</td>
                  {montants && <>
                    <td style={{ textAlign: 'right' }}>{l.salaireCents == null ? 'pas de salaire' : f(l.retenueAbsenceCents)}</td>
                    <td style={{ textAlign: 'right' }}>{l.montantHsCents == null ? (l.hsCompteesMin ? 'selon décision PDG' : '—') : f(l.montantHsCents)}</td>
                  </>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
